import { DownloadSimple, ShieldCheck, Sparkle, MagnifyingGlassPlus, MagnifyingGlassMinus } from "@phosphor-icons/react";
import { formatLocalPrice, getDownloadCtaLabel } from "@/lib/localCurrency";
import ResumePreview from "./ResumePreview";

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 2.0;
const ZOOM_STEP = 0.1;

const clampZoom = (v) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(v * 100) / 100));

export default function PreviewPanel({
  previewRef,
  resume,
  template,
  pageSize,
  density,
  fontScale,
  fontFamily,
  descriptionFontFamily,
  lineSpacing,
  h1FontWeight,
  h2FontWeight,
  bodyFontWeight,
  h1Color,
  h2Color,
  jobTitleFontSize,
  jobTitleFontWeight,
  jobTitleColor,
  jobTitleFontFamily,
  companyFontSize,
  companyFontWeight,
  companyColor,
  companyFontFamily,
  resumeNameFontSize,
  resumeNameFontFamily,
  sectionHeaderFontSize,
  sectionHeaderFontFamily,
  descriptionFontSize,
  descriptionFontWeight,
  descriptionColor,
  contactInfoFontSize,
  contactInfoFontWeight,
  contactInfoColor,
  contactInfoFontFamily,
  workDescriptionFontSize,
  workDescriptionFontWeight,
  workDescriptionColor,
  workDescriptionFontFamily,
  dateFontSize,
  dateFontWeight,
  dateColor,
  dateFontFamily,
  locationFontSize,
  locationFontWeight,
  locationColor,
  locationFontFamily,
  bodyTypography,
  previewZoom = 1,
  setPreviewZoom = () => {},
  paidClean,
  payCfg,
  localCurrency,
  onMovedSectionsChange,
  onOverflowChange,
  hasContent,
  downloading,
  downloadBlocked,
  paidDownloadsLeft,
  paidQuotaExhausted,
  paidDownloadLimit,
  onCleanDownload,
  onPaidDownload,
  mobileView,
}) {
  const discount = payCfg.discount || null;
  const effectiveUsd = discount ? discount.final_usd : payCfg.amount_usd;
  const localPriceSuffix = localCurrency
    ? ` · ~${formatLocalPrice(Number(effectiveUsd), localCurrency)}`
    : "";
  // Localized primary CTA: "Download Resume for ₹99" (India) or
  // "Download Resume for USD $1" (everyone else). Same helper as Builder.js
  // so both entry points stay in lock-step.
  const downloadCtaLabel = getDownloadCtaLabel(localCurrency, discount);

  const zoomOut = () => setPreviewZoom(clampZoom(previewZoom - ZOOM_STEP));
  const zoomIn = () => setPreviewZoom(clampZoom(previewZoom + ZOOM_STEP));
  const zoomReset = () => setPreviewZoom(1);

  return (
    <section
      className={`${mobileView === "preview" ? "block" : "hidden"} lg:block bg-neutral-100 h-[calc(100vh-56px)] overflow-y-auto`}
      data-testid="preview-column"
    >
      <div className="p-2 sm:p-6 lg:p-10 flex justify-center">
        <div className="w-full max-w-[900px]">
          <div
            className="mb-3 sm:mb-4 flex flex-wrap items-center justify-between gap-2"
            data-testid="preview-label-bar"
          >
            <div className="label-mono flex items-center gap-2 min-w-0">
              <Sparkle size={12} weight="fill" className="text-[#FF2A00] shrink-0" />
              <span className="truncate">Live preview / {template}</span>
            </div>
            <div className="flex items-center gap-2">
              <div
                className="flex items-center border border-black/10 bg-white"
                data-testid="preview-zoom-controls"
              >
                <button
                  type="button"
                  onClick={zoomOut}
                  disabled={previewZoom <= ZOOM_MIN + 0.001}
                  className="px-2 py-1.5 text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Zoom out"
                  aria-label="Zoom out"
                  data-testid="preview-zoom-out"
                >
                  <MagnifyingGlassMinus size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={zoomReset}
                  className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-widest text-neutral-700 hover:bg-neutral-100 border-x border-black/10 min-w-[52px]"
                  title="Reset zoom to 100%"
                  aria-label="Reset zoom"
                  data-testid="preview-zoom-reset"
                >
                  {Math.round(previewZoom * 100)}%
                </button>
                <button
                  type="button"
                  onClick={zoomIn}
                  disabled={previewZoom >= ZOOM_MAX - 0.001}
                  className="px-2 py-1.5 text-neutral-700 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Zoom in"
                  aria-label="Zoom in"
                  data-testid="preview-zoom-in"
                >
                  <MagnifyingGlassPlus size={14} weight="bold" />
                </button>
              </div>
              <div className="label-mono hidden md:block">
                {pageSize === "a4" ? "A4 · 210×297mm" : "US Letter · 8.5×11"} · {density} · {Math.round(fontScale * 10)}pt
              </div>
            </div>
          </div>

          {/* Zoom wrapper: uses CSS `zoom` (widely supported in modern browsers
              incl. Chromium & Safari; Firefox since v126). Unlike CSS transform
              this properly resizes the layout box so scroll behaviour and the
              download button below stay correctly positioned. Print CSS resets
              zoom to 1 so PDF output is unaffected. */}
          <div className="preview-zoom-outer w-full overflow-x-auto">
            <div
              className="preview-zoom-inner"
              style={{ zoom: previewZoom }}
              data-testid="preview-zoom-wrapper"
            >
              <div ref={previewRef} data-testid="resume-preview">
                <ResumePreview
                  resume={resume}
                  template={template}
                  pageSize={pageSize}
                  density={density}
                  fontScale={fontScale}
                  fontFamily={fontFamily}
                  descriptionFontFamily={descriptionFontFamily}
                  lineSpacing={lineSpacing}
                  h1FontWeight={h1FontWeight}
                  h2FontWeight={h2FontWeight}
                  bodyFontWeight={bodyFontWeight}
                  h1Color={h1Color}
                  h2Color={h2Color}
                  jobTitleFontSize={jobTitleFontSize}
                  jobTitleFontWeight={jobTitleFontWeight}
                  jobTitleColor={jobTitleColor}
                  jobTitleFontFamily={jobTitleFontFamily}
                  companyFontSize={companyFontSize}
                  companyFontWeight={companyFontWeight}
                  companyColor={companyColor}
                  companyFontFamily={companyFontFamily}
                  resumeNameFontSize={resumeNameFontSize}
                  resumeNameFontFamily={resumeNameFontFamily}
                  sectionHeaderFontSize={sectionHeaderFontSize}
                  sectionHeaderFontFamily={sectionHeaderFontFamily}
                  descriptionFontSize={descriptionFontSize}
                  descriptionFontWeight={descriptionFontWeight}
                  descriptionColor={descriptionColor}
                  contactInfoFontSize={contactInfoFontSize}
                  contactInfoFontWeight={contactInfoFontWeight}
                  contactInfoColor={contactInfoColor}
                  contactInfoFontFamily={contactInfoFontFamily}
                  workDescriptionFontSize={workDescriptionFontSize}
                  workDescriptionFontWeight={workDescriptionFontWeight}
                  workDescriptionColor={workDescriptionColor}
                  workDescriptionFontFamily={workDescriptionFontFamily}
                  dateFontSize={dateFontSize}
                  dateFontWeight={dateFontWeight}
                  dateColor={dateColor}
                  dateFontFamily={dateFontFamily}
                  locationFontSize={locationFontSize}
                  locationFontWeight={locationFontWeight}
                  locationColor={locationColor}
                  locationFontFamily={locationFontFamily}
                  bodyTypography={bodyTypography}
                  showWatermark={!paidClean && payCfg.enabled}
                  onMovedSectionsChange={onMovedSectionsChange}
                  onOverflowChange={onOverflowChange}
                />
              </div>
            </div>
          </div>

          <div className="mt-6" data-testid="preview-download-wrapper">
            {paidClean ? (
              <div className="flex flex-col items-center gap-2">
                <button
                  onClick={onCleanDownload}
                  disabled={downloadBlocked}
                  className="btn-primary"
                  data-testid="preview-download-button"
                >
                  <DownloadSimple size={16} weight="bold" />
                  {downloading
                    ? "Preparing…"
                    : !hasContent
                      ? "Add your name to download"
                      : paidQuotaExhausted
                        ? "Download limit reached"
                        : `Download PDF · ${paidDownloadsLeft} of ${paidDownloadLimit} left`}
                </button>
                <div className="label-mono text-neutral-500" data-testid="paid-clean-badge">
                  <ShieldCheck size={11} weight="fill" className="inline text-[#2400FF] -mt-0.5 mr-1" />
                  Watermark removed · <b data-testid="paid-quota-left">{paidDownloadsLeft}</b> of {paidDownloadLimit} downloads left
                </div>
                {paidQuotaExhausted && (
                  <button
                    onClick={onPaidDownload}
                    disabled={downloading || !hasContent}
                    className="btn-secondary text-xs"
                    data-testid="preview-repay-button"
                  >
                    Buy {paidDownloadLimit} more · Pay {discount ? `USD ${discount.final_usd.toFixed(2)}` : `USD ${payCfg.amount_usd}`}{localPriceSuffix}
                  </button>
                )}
              </div>
            ) : payCfg.enabled ? (
              <div className="flex flex-col items-center gap-2" data-testid="paywall-cta">
                {discount && (
                  <div
                    className="inline-flex items-center gap-2 bg-[#FF2A00]/10 border border-[#FF2A00]/40 text-[#FF2A00] px-3 py-1"
                    data-testid="discount-banner-preview"
                  >
                    <span className="font-mono text-[10px] font-bold uppercase tracking-widest">
                      {discount.percent}% OFF
                    </span>
                    <span className="text-xs">{discount.label}</span>
                    {discount.ends_at && (
                      <span className="font-mono text-[10px] text-neutral-600">
                        · ends {new Date(discount.ends_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
                <button
                  onClick={onPaidDownload}
                  disabled={downloadBlocked}
                  className="btn-primary relative"
                  data-testid="preview-download-paid-button"
                  title={`${downloadCtaLabel} — one-time payment unlocks the watermark-free PDF for up to ${paidDownloadLimit} downloads in this session`}
                >
                  <ShieldCheck size={14} weight="bold" />
                  {downloading
                    ? "Preparing…"
                    : !hasContent
                      ? "Add your name to unlock download"
                      : discount ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="line-through opacity-60">USD ${payCfg.amount_usd}</span>
                          <span>{downloadCtaLabel}</span>
                        </span>
                      ) : downloadCtaLabel}
                </button>
                <div className="label-mono text-neutral-500 text-center">
                  One payment unlocks {paidDownloadLimit} watermark-free downloads for this session.
                </div>
              </div>
            ) : (
              <div className="text-center">
                <button
                  onClick={onCleanDownload}
                  disabled={downloadBlocked}
                  className="btn-primary"
                  data-testid="preview-download-button"
                >
                  <DownloadSimple size={16} weight="bold" />
                  {downloading
                    ? "Preparing…"
                    : !hasContent
                      ? "Add your name to download"
                      : "Download PDF"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
